/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ImportPinListsModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/

import { useState, useRef, useEffect } from 'react';
import { Upload, X, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { buildApiUrl, getApiHeaders } from '../../config/api';

interface ImportPinListsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportResults {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export default function ImportPinListsModal({ isOpen, onClose, onSuccess }: ImportPinListsModalProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [importMode, setImportMode] = useState<'skip' | 'update' | 'replace'>('skip');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setCsvContent(null);
      setRowCount(0);
      setResults(null);
      setError(null);
      setImportMode('skip');
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError(t('pinLists.import.invalidFileType'));
      return;
    }

    setSelectedFile(file);
    setError(null);
    setResults(null);

    // Read CSV file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        
        // Validate CSV has content
        if (!content || content.trim().length === 0) {
          setError(t('pinLists.import.invalidFileFormat'));
          setCsvContent(null);
          return;
        }

        // Count rows (excluding header)
        const lines = content.split('\n').filter(line => line.trim());
        const count = lines.length > 1 ? lines.length - 1 : 0;
        
        setRowCount(count);
        setCsvContent(content);
      } catch (err) {
        setError(t('pinLists.import.invalidCsv'));
        setCsvContent(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvContent) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('/api/telephony/pin-lists/import'), {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          csvContent: csvContent,
          mode: importMode,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t('pinLists.import.error'));
      }

      setResults(result.data);
      
      // Don't auto-close - let user review results and close manually
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pinLists.import.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // If there are results, refresh the parent data
    if (results && results.created > 0) {
      onSuccess();
    }
    
    setSelectedFile(null);
    setCsvContent(null);
    setRowCount(0);
    setResults(null);
    setError(null);
    setImportMode('skip');
    onClose();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Upload className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {t('pinLists.import.title')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('pinLists.import.description')}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* File Selection */}
          {!csvContent && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  {t('pinLists.import.selectFile')}
                </p>
                <button
                  onClick={handleBrowse}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {t('pinLists.import.browse')}
                </button>
                <p className="text-xs text-muted-foreground mt-4">
                  {t('pinLists.import.fileFormat')}
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {error}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* File Selected */}
          {csvContent && !results && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-foreground">{selectedFile?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('pinLists.import.rowsFound', { count: rowCount })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setCsvContent(null);
                    setRowCount(0);
                    setError(null);
                  }}
                  className="p-2 hover:bg-background rounded-lg transition-colors"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>

              {/* Import Mode Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {t('pinLists.import.importMode')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="importMode"
                      value="skip"
                      checked={importMode === 'skip'}
                      onChange={(e) => setImportMode(e.target.value as 'skip')}
                      className="text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {t('pinLists.import.modeSkip')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('pinLists.import.modeSkipDescription')}
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="importMode"
                      value="update"
                      checked={importMode === 'update'}
                      onChange={(e) => setImportMode(e.target.value as 'update')}
                      className="text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {t('pinLists.import.modeUpdate')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('pinLists.import.modeUpdateDescription')}
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={(e) => setImportMode(e.target.value as 'replace')}
                      className="text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-foreground">
                        {t('pinLists.import.modeReplace')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('pinLists.import.modeReplaceDescription')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {error}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div className="flex-1">
                  <p className="font-medium text-green-800 dark:text-green-200">
                    {t('pinLists.import.completed')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold text-foreground">{results.total}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('pinLists.import.total')}
                  </p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {results.created}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('pinLists.import.created')}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {results.updated}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('pinLists.import.updated')}
                  </p>
                </div>
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {results.skipped}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('pinLists.import.skipped')}
                  </p>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t('pinLists.import.errors', { count: results.errors.length })}
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {results.errors.map((err, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm"
                      >
                        <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-red-800 dark:text-red-200">{err}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('pinLists.import.reviewMessage')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            {results ? t('common.close') : t('common.cancel')}
          </button>
          {csvContent && !results && (
            <button
              onClick={handleImport}
              disabled={isProcessing}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  {t('pinLists.import.processing')}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  {t('pinLists.import.import')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

